import { Link, useParams } from "react-router-dom";
import Seo, { breadcrumbLd, localBusinessLd, faqLd } from "../lib/seo";
import { SITE, IMG } from "../lib/site";
import { LOCATIONS, PRODUCTS } from "../data/content";
import { Breadcrumbs } from "../components/site/Layout";
import { PageHero, CtaBand, RelatedGrid } from "../components/site/Sections";
import { Reveal, Overline } from "../components/site/Reveal";
import NotFound from "./NotFound";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { Phone, Mail, MapPin } from "lucide-react";

export function Locations() {
  return (
    <>
      <Seo title="Hologram Sticker Manufacturer Across the UAE | NanoMax Locations"
        description="NanoMax Holo Solutions serves businesses across the UAE — Sharjah, Dubai, Abu Dhabi, Ajman, Ras Al Khaimah, Fujairah, Umm Al Quwain and Al Ain."
        path="/locations" jsonLd={breadcrumbLd([{ name: "Home", path: "/" }, { name: "Locations", path: "/locations" }])} />
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "UAE Locations" }]} />
      <PageHero overline="UAE Service Areas" title="Serving Businesses Across the UAE"
        subtitle="Based in Sharjah Media City, we supply custom hologram stickers and security labels to businesses throughout the United Arab Emirates." />
      <section className="px-6 md:px-10 lg:px-16 py-20 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {LOCATIONS.map((l, i) => (
          <Reveal key={l.slug} delay={i * 0.04}>
            <Link to={`/locations/${l.slug}`} className="card p-7 h-full block group" data-testid={`location-card-${i}`}>
              <h2 className="font-head text-xl font-semibold group-hover:text-holo-cyan transition-colors">{l.city}</h2>
              <p className="text-sm text-[#8A8A9B] mt-3 leading-relaxed">{l.tile}</p>
            </Link>
          </Reveal>
        ))}
      </section>
      <CtaBand sourcePage="/locations" />
    </>
  );
}

export function LocationDetail() {
  const { slug } = useParams();
  const l = LOCATIONS.find((x) => x.slug === slug);
  if (!l) return <NotFound />;
  const path = `/locations/${l.slug}`;
  const faqs = [
    { q: `Can businesses in ${l.city} order custom hologram stickers?`, a: `Yes. We supply custom hologram stickers and security labels to ${l.city} businesses, coordinated from our Sharjah Media City base.` },
    { q: `How do I request a quote in ${l.city}?`, a: `Use the quote form on this page or WhatsApp us at ${SITE.phone} with your product type, quantity and security requirements.` },
  ];
  return (
    <>
      <Seo title={`${l.h1} | NanoMax Holo Solutions`} description={l.intro.slice(0, 155)} path={path}
        jsonLd={[localBusinessLd, breadcrumbLd([{ name: "Home", path: "/" }, { name: "Locations", path: "/locations" }, { name: l.city, path }]), faqLd(faqs)]} />
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Locations", path: "/locations" }, { name: l.city }]} />
      <PageHero overline={`${l.city}, UAE`} title={l.h1} subtitle={l.intro} />
      <section className="px-6 md:px-10 lg:px-16 py-16 grid lg:grid-cols-3 gap-6">
        <Reveal>
          <div className="card p-8 h-full">
            <Overline>Service Coverage</Overline>
            <p className="text-[#8A8A9B] mt-4 leading-relaxed">{l.coverage}</p>
          </div>
        </Reveal>
        <Reveal delay={0.06}>
          <div className="card p-8 h-full">
            <Overline>Relevant Industries</Overline>
            <ul className="mt-4 space-y-2">{l.industries.map((x) => <li key={x} className="text-[#C9C9D4] flex gap-3"><span className="text-holo-cyan">—</span>{x}</li>)}</ul>
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="card p-8 h-full">
            <Overline>Contact</Overline>
            <div className="mt-4 space-y-3 text-sm">
              <a href={`tel:${SITE.phoneRaw}`} className="flex items-center gap-2 text-[#C9C9D4] hover:text-holo-cyan transition-colors"><Phone size={15} /> {SITE.phone}</a>
              <a href={`mailto:${SITE.email}`} className="flex items-center gap-2 text-[#C9C9D4] hover:text-holo-cyan transition-colors"><Mail size={15} /> {SITE.email}</a>
              <div className="flex items-center gap-2 text-[#8A8A9B]"><MapPin size={15} /> {SITE.address}</div>
            </div>
          </div>
        </Reveal>
      </section>
      <section className="px-6 md:px-10 lg:px-16 py-16 border-t border-white/10 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4"><Overline>FAQ</Overline><h2 className="font-head text-2xl md:text-3xl font-bold mt-4">{l.city} enquiries.</h2></div>
        <div className="lg:col-span-8">
          <Accordion type="single" collapsible>
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`f${i}`} className="border-white/10">
                <AccordionTrigger className="text-left font-head hover:text-holo-cyan hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[#8A8A9B] text-sm leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
      <RelatedGrid title="Popular Products" items={PRODUCTS.slice(0, 4).map((x) => ({ name: x.name, desc: x.tile, to: `/products/${x.slug}` }))} />
      <CtaBand sourcePage={path} />
    </>
  );
}
